import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GoogleLogin } from '@react-oauth/google';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { LogIn, ArrowLeft, Mail, Eye, EyeOff, CheckCircle, Send, UserPlus } from 'lucide-react';
import { googleSignIn, signup, login, resendVerification, selectAuthStatus, selectAuthError, clearError } from '../features/auth/authSlice';
import SEO from './SEO';
import './LoginPage.css';

// Views: 'initial', 'signin', 'signup', 'verification'
const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const status = useSelector(selectAuthStatus);
  const error = useSelector(selectAuthError);

  // Get redirect destination from state or default to dashboard
  const from = location.state?.from?.pathname || '/dashboard';

  // Determine initial view based on URL path or state
  const getInitialView = () => {
    if (location.pathname === '/signup') return 'signup';
    if (location.pathname === '/login') return 'signin';
    return location.state?.view || 'initial';
  };
  const [view, setView] = useState(getInitialView);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [validationError, setValidationError] = useState('');

  // Get existing anonymous token for account linking
  const existingToken = localStorage.getItem('cleanStudioToken');

  const validatePassword = (pwd) => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number';
    }
    return '';
  };

  const validateEmail = (email) => {
    const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return pattern.test(email);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');
    setResendSuccess(false);
    dispatch(clearError());

    // Validate email
    if (!validateEmail(email)) {
      setValidationError('Please enter a valid email address');
      return;
    }

    // Validate password for signup
    if (view === 'signup') {
      const pwdError = validatePassword(password);
      if (pwdError) {
        setValidationError(pwdError);
        return;
      }
      if (!name.trim()) {
        setValidationError('Please enter your name');
        return;
      }
    }

    try {
      if (view === 'signup') {
        const result = await dispatch(
          signup({
            email,
            password,
            name: name.trim(),
            existingToken,
          })
        ).unwrap();

        if (result && result.email_verification_required) {
          // Show verification message
          setVerificationEmail(email);
          setView('verification');
        }
      } else {
        const result = await dispatch(
          login({
            email,
            password,
            existingToken,
          })
        ).unwrap();

        if (result) {
          navigate(from, { replace: true });
        }
      }
    } catch (err) {
      console.error('Auth failed:', err);
      // Check if error is due to unverified email (from signup or login)
      // err can be an object { message, data } from rejectWithValue or an Error
      const errorData = err?.data || err;
      const errorMessage = err?.message || err;

      if (errorData?.email_verification_pending || errorData?.email_verification_required) {
        setVerificationEmail(errorData.email || email);
        setView('verification');
      } else if (typeof errorMessage === 'string' && errorMessage.includes('verify your email')) {
        setVerificationEmail(email);
        setView('verification');
      }
    }
  };

  const handleResendVerification = async () => {
    setResendSuccess(false);
    dispatch(clearError());

    try {
      await dispatch(resendVerification({ email: verificationEmail })).unwrap();
      setResendSuccess(true);
    } catch (err) {
      console.error('Resend failed:', err);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      dispatch(clearError());
      const result = await dispatch(
        googleSignIn({
          idToken: credentialResponse.credential,
          existingToken: existingToken,
        })
      ).unwrap();

      // Redirect to main page on success
      if (result) {
        navigate(from, { replace: true });
      }
    } catch (err) {
      console.error('Google sign-in failed:', err);
    }
  };

  const handleGoogleError = () => {
    console.error('Google Sign-In failed');
  };

  const handleGoBack = () => {
    if (view === 'verification') {
      setView('signup');
      setResendSuccess(false);
    } else if (view === 'signin' || view === 'signup') {
      setView('initial');
      setValidationError('');
      dispatch(clearError());
    } else {
      navigate(-1);
    }
  };

  const goToSignIn = () => {
    setView('signin');
    setValidationError('');
    setResendSuccess(false);
    dispatch(clearError());
  };

  const goToSignUp = () => {
    setView('signup');
    setValidationError('');
    setResendSuccess(false);
    dispatch(clearError());
  };

  // Verification message view
  if (view === 'verification') {
    return (
      <>
        <SEO
          title="Verify Your Email - myCleanStudio"
          description="Please verify your email address to complete registration"
          url="/login"
        />
        <div className="login-page">
          <div className="login-container">
            <div className="login-header">
              <div className="login-icon verification-icon">
                <Mail size={32} color="white" />
              </div>
              <h2 className="login-title">Check your email</h2>
              <p className="login-subtitle">
                We sent a verification link to
              </p>
              <p className="verification-email">{verificationEmail}</p>
            </div>

            <div className="verification-info">
              <CheckCircle size={20} className="check-icon" />
              <p>Click the link in the email to verify your account and sign in.</p>
            </div>

            {resendSuccess && (
              <div className="resend-success">
                Verification email sent!
              </div>
            )}

            {error && !resendSuccess && (
              <div className="login-error">
                {error}
              </div>
            )}

            <div className="verification-actions">
              <button
                type="button"
                onClick={handleResendVerification}
                className="resend-btn"
                disabled={status === 'loading'}
              >
                {status === 'loading' ? (
                  <>
                    <span className="btn-spinner"></span>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Resend verification email
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleGoBack}
                className="go-back-btn"
              >
                <ArrowLeft size={16} />
                Back to sign up
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Initial view with Sign in / Create account buttons
  if (view === 'initial') {
    return (
      <>
        <SEO
          title="Sign In - myCleanStudio"
          description="Sign in to myCleanStudio to access your saved content analysis history and check your videos for platform compliance."
          keywords="sign in, login, myCleanStudio account, content compliance"
          url="/login"
        />
        <div className="login-page">
          <div className="login-container">
            <div className="login-header">
              <div className="login-icon">
                <LogIn size={32} color="white" />
              </div>
              <h2 className="login-title">Welcome to myCleanStudio</h2>
              <p className="login-subtitle">
                Sign in to access your saved analysis history
              </p>
            </div>

            {error && (
              <div className="login-error">
                {error}
              </div>
            )}

            <div className="oauth-buttons">
              <button
                type="button"
                onClick={goToSignIn}
                className="primary-auth-btn"
              >
                <LogIn size={18} />
                Sign in
              </button>

              <button
                type="button"
                onClick={goToSignUp}
                className="secondary-auth-btn"
              >
                <UserPlus size={18} />
                Create account
              </button>

              <div className="auth-divider">
                <span>or</span>
              </div>

              <div className="google-btn-wrapper">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap={false}
                  text="continue_with"
                  shape="rectangular"
                  width="100%"
                  size="large"
                  theme="outline"
                  disabled={status === 'loading'}
                />
              </div>

              {status === 'loading' && (
                <div className="loading-overlay">
                  <div className="loading-spinner"></div>
                  <span>Signing in...</span>
                </div>
              )}
            </div>

            <div className="login-footer">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="go-back-btn"
              >
                <ArrowLeft size={16} />
                Go back
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Sign in or Sign up form view
  const isSignUp = view === 'signup';

  return (
    <>
      <SEO
        title={isSignUp ? "Create Account - myCleanStudio" : "Sign In - myCleanStudio"}
        description={isSignUp ? "Create your myCleanStudio account" : "Sign in to myCleanStudio"}
        url="/login"
      />
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <div className="login-icon">
              {isSignUp ? <UserPlus size={32} color="white" /> : <LogIn size={32} color="white" />}
            </div>
            <h2 className="login-title">
              {isSignUp ? 'Create your account' : 'Sign in to your account'}
            </h2>
            <p className="login-subtitle">
              {isSignUp
                ? 'Join to save your analysis history'
                : 'Enter your credentials to continue'}
            </p>
          </div>

          {(error || validationError) && (
            <div className="login-error">
              {validationError || error}
            </div>
          )}

          <div className="oauth-buttons">
            <form onSubmit={handleEmailSubmit} className="email-auth-form">
              {isSignUp && (
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    disabled={status === 'loading'}
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={status === 'loading'}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={isSignUp ? 'Min. 8 characters' : 'Your password'}
                    disabled={status === 'loading'}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {isSignUp && (
                  <p className="password-hint">
                    Must include uppercase, lowercase, and a number
                  </p>
                )}
                {!isSignUp && (
                  <Link to="/forgot-password" className="forgot-password-link">
                    Forgot password?
                  </Link>
                )}
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={status === 'loading'}
              >
                {status === 'loading' ? (
                  <>
                    <span className="btn-spinner"></span>
                    {isSignUp ? 'Creating account...' : 'Signing in...'}
                  </>
                ) : (
                  isSignUp ? 'Create account' : 'Sign in'
                )}
              </button>
            </form>

            <div className="auth-divider">
              <span>or</span>
            </div>

            <div className="google-btn-wrapper">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap={false}
                text={isSignUp ? 'signup_with' : 'signin_with'}
                shape="rectangular"
                width="100%"
                size="large"
                theme="outline"
                disabled={status === 'loading'}
              />
            </div>

            {status === 'loading' && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <span>{isSignUp ? 'Creating account...' : 'Signing in...'}</span>
              </div>
            )}
          </div>

          <div className="login-footer">
            <button
              type="button"
              onClick={isSignUp ? goToSignIn : goToSignUp}
              className="toggle-mode-btn"
            >
              {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>

            <button
              type="button"
              onClick={handleGoBack}
              className="go-back-btn"
            >
              <ArrowLeft size={16} />
              Go back
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
